import { useEffect, useState } from "react";
import { Link2, Mail, Copy, Check, X, Loader2, Send, ExternalLink } from "lucide-react";
import { api, PUBLIC_APP_URL } from "../../lib/api";

/** Share the founder's Capital Readiness Report.
 *
 *  - "Link" tab: lazily creates a public share token, shows the share URL and a Copy button.
 *  - "Email" tab: sends the share link to one recipient via Resend with an optional message.
 *
 *  Tokens expire after 30 days and are rotated automatically when the modal is opened.
 */
export default function ShareReportModal({ score, onClose }) {
  const [tab, setTab] = useState("link");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [share, setShare] = useState(null); // { token, url, expires_at }
  const [copied, setCopied] = useState(false);

  // Email tab state
  const [recipient, setRecipient] = useState("");
  const [message, setMessage] = useState("");
  const [sendResult, setSendResult] = useState(null);

  // Lazily mint a token when the modal opens
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true); setErr("");
      try {
        const { data } = await api.post("/founder/readiness/share-token");
        if (!cancelled) setShare(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Couldn't create share link.");
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fullUrl = share?.url || (share?.token ? `${PUBLIC_APP_URL}/r/${share.token}` : "");

  const copy = async () => {
    if (!fullUrl) return;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      window.alert("Copy failed — long-press the URL to copy manually.");
    }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    setErr(""); setSendResult(null);
    const to = recipient.trim();
    if (!to || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) {
      setErr("Please enter a valid recipient email address.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/founder/readiness/share-email", {
        email: to, message: message.trim() || null,
      });
      setSendResult(data);
      if (data?.url && !share?.url) setShare({ url: data.url });
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Couldn't send the email.");
    } finally {
      setBusy(false);
    }
  };

  const expiresOn = share?.expires_at
    ? new Date(share.expires_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
    : "30 days from now";

  return (
    <div
      data-testid="share-report-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-neutral-900">Share your Capital Readiness Report</h3>
            <p className="mt-0.5 text-xs text-neutral-500">
              Read-only. No sign-in required. Link expires {expiresOn}.
            </p>
          </div>
          <button
            data-testid="share-report-close"
            onClick={onClose}
            className="rounded-md p-1 text-neutral-400 hover:bg-neutral-100"
          >
            <X className="h-5 w-5"/>
          </button>
        </div>

        {/* Tabs */}
        <div className="mt-5 grid grid-cols-2 gap-1 rounded-lg bg-neutral-100 p-1">
          <button
            data-testid="share-tab-link"
            onClick={() => setTab("link")}
            className={`inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition ${
              tab === "link" ? "bg-white text-[#00828F] shadow-sm" : "text-neutral-600 hover:text-neutral-900"
            }`}
          >
            <Link2 className="h-4 w-4"/> Copy link
          </button>
          <button
            data-testid="share-tab-email"
            onClick={() => setTab("email")}
            className={`inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition ${
              tab === "email" ? "bg-white text-[#00828F] shadow-sm" : "text-neutral-600 hover:text-neutral-900"
            }`}
          >
            <Mail className="h-4 w-4"/> Email it
          </button>
        </div>

        {tab === "link" && (
          <div className="mt-5">
            {busy && !share && (
              <p className="flex items-center gap-2 text-sm text-neutral-500">
                <Loader2 className="h-4 w-4 animate-spin"/> Creating your share link…
              </p>
            )}
            {share && (
              <>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
                  Public link to your report
                </label>
                <div className="mt-2 flex items-stretch gap-2">
                  <input
                    data-testid="share-link-input"
                    readOnly
                    value={fullUrl}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 truncate rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm font-mono text-neutral-700 focus:border-[#00828F] focus:outline-none"
                  />
                  <button
                    data-testid="share-link-copy"
                    onClick={copy}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#004B62] px-3 py-2 text-sm font-bold text-white hover:bg-[#00303F]"
                  >
                    {copied ? <Check className="h-4 w-4"/> : <Copy className="h-4 w-4"/>}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                <a
                  href={fullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="share-link-preview"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#00828F] hover:underline"
                >
                  Preview the public report <ExternalLink className="h-3.5 w-3.5"/>
                </a>
              </>
            )}
            <p className="mt-4 rounded-lg bg-neutral-50 p-3 text-xs leading-relaxed text-neutral-600">
              You scored <b>{score}/100</b>. Anyone with this link can view your readiness
              report (score, strengths, areas to improve, funder shortlist, roadmap).
              They cannot see your documents, contact details or team. Generate a new
              link any time to revoke the old one.
            </p>
          </div>
        )}

        {tab === "email" && (
          <form className="mt-5" onSubmit={sendEmail}>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
              Recipient email
            </label>
            <input
              data-testid="share-email-recipient"
              type="email"
              required
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="investor@fund.com"
              className="mt-2 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:border-[#00828F] focus:outline-none"
            />
            <label className="mt-4 block text-xs font-semibold uppercase tracking-wider text-neutral-500">
              Personal note <span className="text-neutral-400 normal-case">(optional)</span>
            </label>
            <textarea
              data-testid="share-email-message"
              rows={4}
              maxLength={600}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={"Hi, sharing my Clinq Capital Readiness Report — would love your feedback."}
              className="mt-2 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:border-[#00828F] focus:outline-none"
            />
            <p className="mt-1 text-[11px] text-neutral-400">{message.length}/600</p>

            {err && <p className="mt-3 text-sm text-red-600" data-testid="share-error">{err}</p>}

            {sendResult?.ok && (
              <div data-testid="share-email-success"
                   className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                Sent to <b>{sendResult.recipient || recipient}</b>. Reply-to is set to you.
              </div>
            )}
            {sendResult && sendResult.ok === false && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                Couldn&apos;t deliver via email ({sendResult.error || "unknown error"}), but your
                share link is ready — copy it from the &ldquo;Copy link&rdquo; tab.
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="rounded-lg bg-neutral-100 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                data-testid="share-email-send"
                type="submit"
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-lg bg-[#004B62] px-5 py-2 text-sm font-bold text-white hover:bg-[#00303F] disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : <Send className="h-4 w-4"/>}
                {busy ? "Sending…" : "Send link"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
